<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    :embedded="embedded">
    <div class="row no-gutters h-100">
      <div class="col-2 p-2">
        <AnalyticsDashboardValueRow>
          <div class="col">
            <ReportFilterPopover
              headerTitle="Daily Payments Value Filter"
              :showApplyButton="true"
              :showResetButton="true"
              :appendToBody="true"
              alignment="left"
              placement="bottom-end"
              @apply="applyPaymentsValueFilter"
              @reset="resetPaymentsValueFilter">
              <template v-slot:popover>
                <ReportDateRangeSelect 
                  :startDate="editingPaymentsValueFilter.startDate"
                  :endDate="editingPaymentsValueFilter.endDate"
                  @updateStartDate="editingPaymentsValueFilter.startDate = $event"
                  @updateEndDate="editingPaymentsValueFilter.endDate = $event"
                />
              </template>
              <template v-slot:filterDisplay>
                <div>
                  <i class="far fa-calendar mr-2"></i>{{appliedPaymentsValueFilter.startDate | formatDate}} - {{appliedPaymentsValueFilter.endDate | formatDate}}
                </div>                
              </template>
            </ReportFilterPopover>
          </div>
        </AnalyticsDashboardValueRow>

        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Total Payments Amount" highlight>
              {{formatCurrency(paymentSummaryData.totalPaymentAmount)}}
              <template v-slot:info>Over {{paymentSummaryData.totalPaymentCount}} payment{{displayCountPlural(paymentSummaryData.totalPaymentCount)}} during the period</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
        <AnalyticsDashboardValueRow>
          <div class="col">
            <AnalyticsDashboardValue title="Average Daily Amount">
              {{formatCurrency(paymentSummaryData.averagePaymentsValue)}}
              <template v-slot:info>Over {{paymentSummaryData.totalPaymentCount}} payment{{displayCountPlural(paymentSummaryData.totalPaymentCount)}} during the period containing {{paymentSummaryData.activePaymentsDayCount}} working day{{displayCountPlural(paymentSummaryData.activePaymentsDayCount)}}</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
        <AnalyticsDashboardValueRow v-if="paymentSummaryData.largestPaymentInfo">
          <div class="col">
            <AnalyticsDashboardValue title="Largest Payment">
              {{formatCurrency(paymentSummaryData.largestPaymentInfo.amount)}}
              <template v-slot:info>Payment <LinkPaymentReceived :id="paymentSummaryData.largestPaymentInfo.id" :name="paymentSummaryData.largestPaymentInfo.sequenceNumber"></LinkPaymentReceived> for {{paymentSummaryData.largestPaymentInfo.contact.name}} on {{formatDate(paymentSummaryData.largestPaymentInfo.datePayment)}}</template>
            </AnalyticsDashboardValue>
          </div>
        </AnalyticsDashboardValueRow>
      </div>
      <div class="col-10">
        <ChartBar
          title="Daily Payments Received"
          :series="paymentDailyValueData.series"
          :xAxis="paymentDailyValueData.xAxis"
          :yAxis="paymentDailyValueData.yAxis"
          :legend="paymentDailyValueData.legend"
          :options="paymentDailyValueData.options"
          :popupEnabled="true"
          :exportEnabled="true"
        />
      </div>
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'

import Utils from '@/utils/utils'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'

import ChartBar from '@/components/charts/chartBar'

import { PaymentTypes, PaymentTypeLookup } from '@/store/types'

export default {
  name: 'PaymentsReceivedDailyChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartBar,
    ReportFilterPopover,
    ReportDateRangeSelect
  },
  props: {
    title: String,
    subTitle: String,
    type: String,
    filter: Object,
    contactId: Number,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean
  },
  data() {
    return {
      paymentSummaryData: {
        totalPaymentAmount: 0,
        totalPaymentCount: 0,
        averagePaymentsValue: 0,
        largestPaymentInfo: null,
        activePaymentsDayCount: 0
      },
      paymentDailyValueData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultPaymentsValueFilter: {    
        startDate: moment().local().subtract(90, 'days').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString()
      },
      editingPaymentsValueFilter: {},
      appliedPaymentsValueFilter: {},
    }
  },
  computed: {
    hasContactFilter() {
      return this.contactId != null
    }
  },
  mounted() {
    this.editingPaymentsValueFilter = _.clone(this.defaultPaymentsValueFilter)
    this.appliedPaymentsValueFilter = _.clone(this.defaultPaymentsValueFilter)

    this.requestChartData()
  },
  beforeDestroy() {
  },
  watch: {
    contactId: function() {
      this.requestChartData()
    },
    filter: function() {
      this.requestChartData()
    }
  },
  methods: {
    applyPaymentsValueFilter() {
      this.appliedPaymentsValueFilter = _.clone(this.editingPaymentsValueFilter)

      this.requestChartData()
    },
    resetPaymentsValueFilter() {
      this.editingPaymentsValueFilter = _.clone(this.defaultPaymentsValueFilter)
      this.appliedPaymentsValueFilter = _.clone(this.defaultPaymentsValueFilter)

      this.requestChartData()
    },
    requestChartData() {
      var paymentValueFilter = {
        type: 'daily',
        startDate: this.appliedPaymentsValueFilter.startDate,
        endDate: this.appliedPaymentsValueFilter.endDate,
        contactId: this.contactId,
        accountLocationId: this.filter ? this.filter.accountLocationIdList : null
      }

      Promise.all([
        this.$apiService.analyticsService.getPaymentsValueChartData(paymentValueFilter),
        this.$apiService.analyticsService.getPaymentsSummary(paymentValueFilter)
      ])
      .then(results => {
        let chartData = results[0].data
        let paymentSummaryData = results[1].data

        this.paymentDailyValueData.xAxis = {
          exportName: 'Date',
          exportData: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM YYYY')),
          data: chartData.map(chartEntry => moment(chartEntry.date).format('DD MMM'))
        }
        this.paymentDailyValueData.yAxis = [
          {
            type: 'value',
            axisLabel: {
              formatter: (value) => {
                return this.formatCurrencySuffix(value, 1);
              }
            }
          },
          { type: 'value', alignTicks: true }
        ]
        this.paymentDailyValueData.series = []

        var chartPaymentTypeList = [
          { type: PaymentTypes.cash, key: 'totalCashAmount' },
          { type: PaymentTypes.creditCard, key: 'totalCCAmount' },
          { type: PaymentTypes.EFTPOS, key: 'totalEFTAmount' },
          { type: PaymentTypes.cheque, key: 'totalChequeAmount' },
          { type: PaymentTypes.external, key: 'totalExtAmount' },
        ]

        chartPaymentTypeList.forEach(chartTypeInfo => {
          this.paymentDailyValueData.series.push({
            type: 'bar',
            name: this.formatLookupName(chartTypeInfo.type, PaymentTypeLookup),
            stack: 'total',
            data: chartData.map(chartEntry => chartEntry[chartTypeInfo.key]),
            emphasis: {
              focus: 'series'
            },
            tooltip: {
              valueFormatter: (value) => {
                return this.formatCurrency(value);
              }
            }
          })
        })
        
        this.paymentDailyValueData.series.push({
          type: 'line',
          name: 'Payments Count',
          yAxisIndex: 1,
          data: chartData.map(chartEntry => chartEntry.totalPaymentCount),
        })

        this.paymentDailyValueData.options = {
          tooltip: {
            formatter: (params) => {
              if(params.length == 0)
                return ''

              let dayTotal = params.reduce((curValue, series) => {
                if(this.paymentDailyValueData.series[series.seriesIndex].name == 'Payments Count')
                  return curValue
                return curValue + series.value
              }, 0)

              let lineList = params.map(series => {
                let value = series.value
                if(this.paymentDailyValueData.series[series.seriesIndex].tooltip && this.paymentDailyValueData.series[series.seriesIndex].tooltip.valueFormatter)
                  value = this.paymentDailyValueData.series[series.seriesIndex].tooltip.valueFormatter(value)

                return `${series.marker}${series.seriesName}<span style="float: right; margin-left: 20px"><b>${value}</b></span>`
              })

              return `${params[0].axisValueLabel}<br />` + lineList.join('<br />') + `<br /><br />Total Payments<span style="float: right; margin-left: 20px"><b>${this.formatCurrency(dayTotal)}</b></span>`
            }
          }
        }

        // Summary info
        this.paymentSummaryData.averagePaymentsValue = 0
        if(chartData.length > 0) {
          this.paymentSummaryData.averagePaymentsValue = chartData.reduce((curValue, paymentInfo) => curValue + paymentInfo.totalAmount, 0) / chartData.length
        }

        this.paymentSummaryData.activePaymentsDayCount = chartData.filter(paymentInfo => paymentInfo.totalPaymentCount > 0).length
        this.paymentSummaryData.totalPaymentAmount = paymentSummaryData.totalPaymentAmount
        this.paymentSummaryData.totalPaymentCount = paymentSummaryData.totalPaymentCount
        this.paymentSummaryData.largestPaymentInfo = paymentSummaryData.largestPayment
      })
    },
  }
}
</script>